import {
  BarChart3,
  Bot,
  CircleDot,
  Gift,
  Home,
  Import,
  Mail,
  MessageCircle,
  PackageCheck,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Store,
  Wand2,
  Zap,
} from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { getPreservedShopifySearch } from "./adminApi";

const navGroups = [
  {
    label: "Home",
    links: [{ to: "/", label: "Overview", icon: Home }],
  },
  {
    label: "Collect",
    links: [
      { to: "/admin/review-requests", label: "Review Requests", icon: Star },
      { to: "/admin/whatsapp-campaigns", label: "WhatsApp", icon: MessageCircle },
      { to: "/admin/email-campaigns", label: "Email", icon: Mail },
      { to: "/admin/forms", label: "Forms", icon: Wand2 },
    ],
  },
  {
    label: "Moderate",
    links: [
      { to: "/admin/reviews", label: "Reviews", icon: ShieldCheck },
      { to: "/admin/moderation", label: "Moderation", icon: Sparkles },
    ],
  },
  {
    label: "Publish",
    links: [
      { to: "/admin/widgets", label: "Widgets", icon: Store },
      { to: "/admin/theme-setup", label: "Theme Setup", icon: Wand2 },
    ],
  },
  {
    label: "Grow",
    links: [
      { to: "/admin/discounts", label: "Discounts", icon: Gift },
      { to: "/admin/referrals", label: "Referrals", icon: Star },
    ],
  },
  {
    label: "Insights",
    links: [
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/admin/ai-tools", label: "AI Tools", icon: Bot },
    ],
  },
  {
    label: "Operations",
    links: [
      { to: "/admin/import-export", label: "Import / Export", icon: Import },
      { to: "/admin/integrations", label: "Integrations", icon: Settings },
      { to: "/admin/settings", label: "Settings", icon: Settings },
    ],
  },
];

export function AdminShell() {
  const location = useLocation();
  const embeddedSearch = getPreservedShopifySearch(location.search);
  const activeLink = findActiveLink(location.pathname);
  const activeGroup = navGroups.find((group) => group.links.some((link) => link.to === activeLink.to))?.label ?? "Workspace";

  return (
    <div className="wr-admin-shell" data-admin-workspace="whatstar">
      <a className="wr-admin-skip-link" href="#whatstar-admin-main">
        Skip navigation
      </a>
      <aside className="wr-sidebar" aria-label="WhatStar navigation">
        <div className="wr-brand">
          <span className="wr-brand__mark">W</span>
          <div>
            <strong>WhatStar</strong>
            <span>Review operations</span>
          </div>
        </div>
        <div className="wr-shell-score" aria-label="Production review system status">
          <span>
            <CircleDot size={13} aria-hidden />
            Production
          </span>
          <strong>WhatsApp-first review engine</strong>
          <small>Requests, moderation, widgets, and rewards in one command center.</small>
        </div>
        <div className="wr-sidebar__scroll">
          {navGroups.map((group) => (
            <nav key={group.label} className="wr-nav-group" aria-label={group.label}>
              <p>{group.label}</p>
              {group.links.map((link) => {
                const Icon = link.icon;
                return (
                  <NavLink key={link.to} to={`${link.to}${embeddedSearch}`} end={link.to === "/"}>
                    <Icon size={16} aria-hidden />
                    <span>{link.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          ))}
        </div>
        <div className="wr-sidebar__footer">
          <PackageCheck size={17} aria-hidden />
          <div>
            <strong>Verified buyers only</strong>
            <span>Delivery-aware queue safety is active.</span>
          </div>
        </div>
      </aside>
      <main className="wr-main" id="whatstar-admin-main" tabIndex={-1}>
        <header className="wr-topbar">
          <div className="wr-topbar__title">
            <span>{activeGroup}</span>
            <h2>{activeLink.label}</h2>
          </div>
          <div className="wr-topbar__actions" aria-label="Quick admin shortcuts">
            <a href={`/admin/review-requests${embeddedSearch}`}>
              <Zap size={15} aria-hidden />
              Send requests
            </a>
            <a href={`/admin/widgets${embeddedSearch}`}>
              <Store size={15} aria-hidden />
              Widgets
            </a>
            <a href={`/admin/settings${embeddedSearch}`}>
              <Settings size={15} aria-hidden />
              Settings
            </a>
          </div>
        </header>
        <Outlet />
      </main>
    </div>
  );
}

function findActiveLink(pathname: string) {
  const links = navGroups.flatMap((group) => group.links);
  if (pathname === "/" || pathname === "") return links[0];
  return (
    links
      .filter((link) => link.to !== "/")
      .sort((a, b) => b.to.length - a.to.length)
      .find((link) => pathname === link.to || pathname.startsWith(`${link.to}/`)) ?? links[0]
  );
}
